import { Loader2, Scale } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import {
  PRODUCT_LAW_KEYS,
  type ProductApplicabilityState,
  type ProductLawKey,
} from '@/shared/hooks/api/product'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { useProductRegulationsJourneyByLaw } from '../hooks/useProductRegulationsJourneyByLaw'
import {
  isRegulationJourneyTrackedLaw,
  type RegulationJourneySummary,
} from '../utils/buildProductRegulationsJourneyByLaw'
import {
  getRegulationJourneyProgressDetailLabel,
  getRegulationJourneyProgressShortLabel,
  REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE,
  RegulationJourneyProgressCircle,
} from './RegulationJourneyProgressCircle'
import { ProductLawApplicabilityBadge } from './ProductLawApplicabilityBadge'
import { StaticLegislationComingSoonBadge } from './static-legislation-briefing/StaticLegislationComingSoonBadge'
import {
  countAssessedLaws,
  sortLawsForRegulationsSummary,
} from '../utils/productApplicabilitySummary'
import { getV2ProductLawPath } from '../utils/routes'
import { isStaticLegislationBriefingLaw } from '../utils/staticLegislationBriefing'

type V2ProductRegulationsSummaryCardProps = {
  productId: string
  applicability: ProductApplicabilityState | undefined
  isLoading: boolean
  isDiscoverRunning: boolean
  isDiscoverDisabled: boolean
  onDiscover: () => void
}

type RegulationsSummaryBodyProps = {
  productId: string
  applicability: ProductApplicabilityState | undefined
  isDiscoverRunning: boolean
  isDiscoverDisabled: boolean
  onDiscover: () => void
}

function RegulationJourneyStatus({
  journey,
  isLoading,
}: {
  journey: RegulationJourneySummary | undefined
  isLoading: boolean
}) {
  const { t } = useTranslation('common')
  const input = { journey, isLoading }
  const shortLabel = getRegulationJourneyProgressShortLabel(t, input)

  if (shortLabel == null) {
    return null
  }

  const detailLabel = getRegulationJourneyProgressDetailLabel(t, input)
  const isComplete = !isLoading && journey?.isComplete === true

  return (
    <p
      className={
        isComplete
          ? 'text-sm text-green-700 dark:text-green-400'
          : 'text-sm text-muted-foreground'
      }
      aria-label={detailLabel}
    >
      {isLoading ? (
        <span className='inline-flex items-center gap-1.5'>
          <Loader2 className='size-3.5 shrink-0 animate-spin' aria-hidden />
          {shortLabel}
        </span>
      ) : (
        shortLabel
      )}
    </p>
  )
}

const COVERAGE_TEASER_INFO_SURFACE_CLASS =
  'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800/80 dark:bg-sky-950/50 dark:text-sky-200'
const COVERAGE_TEASER_INFO_TEXT_CLASS =
  'text-sky-800 dark:text-sky-200'
const COVERAGE_TEASER_INFO_MUTED_TEXT_CLASS =
  'text-sky-700/90 dark:text-sky-300/90'

function RegulationCoverageTeaserRow() {
  const { t } = useTranslation('common')

  return (
    <li className='relative overflow-hidden'>
      <div aria-hidden className='flex select-none items-start blur-[1.5px]'>
        <div className='min-w-0 flex-1 px-3 py-3'>
          <div className='flex items-start gap-1.5'>
            <span
              className='mt-0.5 inline-flex shrink-0 rounded-full border border-muted-foreground/30'
              style={{
                width: REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE,
                height: REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE,
              }}
            />
            <div className='min-w-0 flex-1 space-y-1.5'>
              <p className={`text-sm font-medium ${COVERAGE_TEASER_INFO_TEXT_CLASS}`}>
                {t(
                  'v2.pages.products.detail.regulationsSummary.coverageTeaser.fakeTitle'
                )}
              </p>
              <p className={`text-sm ${COVERAGE_TEASER_INFO_MUTED_TEXT_CLASS}`}>
                {t(
                  'v2.pages.products.detail.regulationsSummary.coverageTeaser.fakeProgress'
                )}
              </p>
              <p className={`text-xs/relaxed ${COVERAGE_TEASER_INFO_MUTED_TEXT_CLASS}`}>
                {t(
                  'v2.pages.products.detail.regulationsSummary.coverageTeaser.fakeSummary'
                )}
              </p>
            </div>
          </div>
        </div>
        <div className='shrink-0 px-3 py-3'>
          <Badge
            variant='outline'
            className={`font-normal ${COVERAGE_TEASER_INFO_SURFACE_CLASS}`}
          >
            {t(
              'v2.pages.products.detail.regulationsSummary.coverageTeaser.fakeBadge'
            )}
          </Badge>
        </div>
      </div>
      <div className='absolute inset-0 flex items-center justify-center bg-sky-50/90 p-4 dark:bg-sky-950/50'>
        <p className={`max-w-md text-center text-sm ${COVERAGE_TEASER_INFO_TEXT_CLASS}`}>
          {t(
            'v2.pages.products.detail.regulationsSummary.coverageTeaser.overlay'
          )}
        </p>
      </div>
    </li>
  )
}

function RegulationsSummaryList({
  productId,
  applicability,
  journeyByLaw,
  isJourneyLoading,
}: {
  productId: string
  applicability: ProductApplicabilityState
  journeyByLaw: Partial<Record<ProductLawKey, RegulationJourneySummary>>
  isJourneyLoading: boolean
}) {
  const { t, i18n } = useTranslation('common')
  const sortedLaws = sortLawsForRegulationsSummary(
    PRODUCT_LAW_KEYS,
    applicability,
    (law) => t(`v2.applicability.laws.${law}`),
    i18n.language
  )

  return (
    <ul className='divide-y overflow-hidden rounded-md border'>
      {sortedLaws.map((law) => {
        const lawApplicability = applicability[law]
        const isApplicable = lawApplicability?.status === 'APPLICABLE'
        const showJourney =
          isApplicable && isRegulationJourneyTrackedLaw(law)
        const journey = showJourney ? journeyByLaw[law] : undefined
        const isJourneyLoadingForLaw = showJourney && isJourneyLoading

        return (
          <li
            key={law}
            className='flex items-start transition-colors hover:bg-muted/50'
          >
            <Link
              to={getV2ProductLawPath(productId, law)}
              className='min-w-0 flex-1 px-3 py-3'
            >
              <div className='flex items-start gap-1.5'>
                {showJourney ? (
                  <RegulationJourneyProgressCircle
                    journey={journey}
                    isLoading={isJourneyLoadingForLaw}
                  />
                ) : (
                  <span
                    className='mt-0.5 inline-flex shrink-0'
                    style={{
                      width: REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE,
                      height: REGULATION_JOURNEY_PROGRESS_CIRCLE_SIZE,
                    }}
                    aria-hidden
                  />
                )}
                <div className='min-w-0 flex-1 space-y-1.5'>
                  <div className='flex min-w-0 flex-wrap items-center gap-2'>
                    <p className='text-sm font-medium'>
                      {t(`v2.applicability.laws.${law}`)}
                    </p>
                    {isStaticLegislationBriefingLaw(law) ? (
                      <StaticLegislationComingSoonBadge />
                    ) : null}
                  </div>
                  {showJourney ? (
                    <RegulationJourneyStatus
                      journey={journey}
                      isLoading={isJourneyLoadingForLaw}
                    />
                  ) : null}
                  <p className='text-xs/relaxed text-muted-foreground'>
                    {t(`v2.applicability.lawSummaries.${law}`)}
                  </p>
                </div>
              </div>
            </Link>
            <div className='shrink-0 px-3 py-3'>
              {lawApplicability != null ? (
                <ProductLawApplicabilityBadge
                  status={lawApplicability.status}
                  source={lawApplicability.source}
                  forceReason={lawApplicability.forceReason}
                />
              ) : (
                <span className='text-sm text-muted-foreground'>
                  {t('v2.applicability.status.UNDECIDED')}
                </span>
              )}
            </div>
          </li>
        )
      })}
      <RegulationCoverageTeaserRow />
    </ul>
  )
}

function RegulationsSummaryBody({
  productId,
  applicability,
  isDiscoverRunning,
  isDiscoverDisabled,
  onDiscover,
}: RegulationsSummaryBodyProps) {
  const { t } = useTranslation('common')
  const { journeyByLaw, isLoading: isJourneyLoading } =
    useProductRegulationsJourneyByLaw(productId, applicability)
  const assessedCount = countAssessedLaws(applicability)
  const totalCount = PRODUCT_LAW_KEYS.length
  const showEmpty = assessedCount === 0 && !isDiscoverRunning

  if (applicability == null) {
    return null
  }

  if (showEmpty) {
    return (
      <Empty className='border border-dashed bg-muted/20'>
        <EmptyHeader>
          <EmptyMedia variant='icon'>
            <Scale aria-hidden />
          </EmptyMedia>
          <EmptyTitle>
            {t('v2.pages.products.detail.regulationsSummary.empty.title')}
          </EmptyTitle>
          <EmptyDescription>
            {t('v2.pages.products.detail.regulationsSummary.empty.description')}
          </EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button
            type='button'
            size='sm'
            disabled={isDiscoverDisabled}
            onClick={onDiscover}
          >
            {t('v2.pages.products.detail.regulationsSummary.discoverButton')}
          </Button>
        </EmptyContent>
      </Empty>
    )
  }

  return (
      <div className='space-y-3'>
        {isDiscoverRunning && (
          <div
            className='flex items-center justify-center gap-2 py-1'
            aria-live='polite'
            aria-busy='true'
          >
            <Loader2
              className='size-4 animate-spin text-muted-foreground'
              aria-hidden
            />
            <p className='text-sm text-muted-foreground'>
              {t('v2.pages.products.detail.regulationsSummary.running', {
                assessed: assessedCount,
                total: totalCount,
              })}
            </p>
          </div>
        )}
        <RegulationsSummaryList
          productId={productId}
          applicability={applicability}
          journeyByLaw={journeyByLaw}
          isJourneyLoading={isJourneyLoading}
        />
      </div>
    )
}

export function V2ProductRegulationsSummaryCard({
  productId,
  applicability,
  isLoading,
  isDiscoverRunning,
  isDiscoverDisabled,
  onDiscover,
}: V2ProductRegulationsSummaryCardProps) {
  const { t } = useTranslation('common')

  return (
    <Card>
      <CardHeader className='pb-3'>
        <CardTitle className='text-base leading-tight'>
          {t('v2.pages.products.detail.regulationsSummary.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='space-y-2'>
            {PRODUCT_LAW_KEYS.map((law) => (
              <Skeleton key={law} className='h-20 w-full' />
            ))}
          </div>
        ) : (
          <RegulationsSummaryBody
            productId={productId}
            applicability={applicability}
            isDiscoverRunning={isDiscoverRunning}
            isDiscoverDisabled={isDiscoverDisabled}
            onDiscover={onDiscover}
          />
        )}
      </CardContent>
    </Card>
  )
}
